/**
 * The listening tour. One conversation at a time: who it was with, what you
 * heard, and anything it raised. The conversation is a dated note on the
 * person (Mandate's stakeholder); each insight, tension or opportunity is an
 * item in the workspace, tagged "heard in the listening tour", so it can be
 * sorted and ranked later like any other item.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMandateStore } from '@/stores/mandate/mandateStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { Select } from '@/components/ds';
import { getAppDefinition } from '../registry';
import { AppShell, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, PrimaryButton } from '../components/primitives';
import { LISTENING_TOUR_QUESTIONS } from './framework';
import { HEARD_KINDS, appendNote, conversationsIn, heardTags, listeningNote, type HeardKind } from './first100Days';
import { useJourney } from './useJourney';
import NoMandate from './NoMandate';

const KIND_LABELS: Record<HeardKind, string> = {
  insight: 'An insight',
  tension: 'A tension',
  opportunity: 'An opportunity',
};

const FIELD =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)]';

export default function ListeningTourScreen() {
  const app = getAppDefinition('first-100-days')!;
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  const createItem = useItemsStore((s) => s.createItem);
  const { loaded, transition, stakeholders } = useJourney();

  const [stakeholderId, setStakeholderId] = useState('');
  const [heard, setHeard] = useState('');
  const [raised, setRaised] = useState<Record<HeardKind, string>>({ insight: '', tension: '', opportunity: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const back = { backTo: '/apps/first-100-days', backLabel: 'Back to the journey' };
  if (!loaded) {
    return (
      <AppShell app={app} {...back}>
        <p className="font-body text-sm text-[var(--text-ter)]">Reading your mandate…</p>
      </AppShell>
    );
  }
  if (!transition) {
    return (
      <AppShell app={app} {...back}>
        <NoMandate />
      </AppShell>
    );
  }

  const person = stakeholders.find((s) => s.id === stakeholderId);
  const logged = stakeholders
    .flatMap((s) => conversationsIn(s.notes).map((c) => ({ ...c, name: s.name })))
    .reverse();

  const save = async () => {
    if (!person || !heard.trim() || !userId) return;
    setBusy(true);
    setError(null);
    const note = listeningNote(new Date(), heard);
    await useMandateStore.getState().updateStakeholder(person.id, { notes: appendNote(person.notes, note) });
    const workspaceId = getCurrentWorkspaceId() ?? undefined;
    let failed = 0;
    for (const kind of HEARD_KINDS) {
      const title = raised[kind].trim();
      if (!title) continue;
      const ok = await createItem({
        title,
        description: `Raised by ${person.name} in the listening tour.`,
        exit_status: 'active',
        tags: heardTags(kind),
        createdBy: userName || userId,
        workspace_id: workspaceId,
        originModule: 'mandate',
      } as Parameters<typeof createItem>[0]);
      if (!ok) failed += 1;
    }
    setBusy(false);
    if (failed > 0) {
      setError('The conversation is saved, but something it raised could not be added. Add it again.');
      return;
    }
    setSaved(person.name);
    setHeard('');
    setRaised({ insight: '', tension: '', opportunity: '' });
    setStakeholderId('');
  };

  return (
    <AppShell app={app} {...back}>
      <ScreenHeading title="Listening tour" subtitle="Listen to understand, not to respond or fix. Log each conversation while it's fresh." />

      {stakeholders.length === 0 ? (
        <div className="mt-4">
          <EmptyState
            title="No one is on your stakeholder map yet"
            hint="Add the people you will meet first: up, across, down and outside."
            action={<PrimaryButton onClick={() => navigate('/apps/mandate/stakeholders')}>Add stakeholders in Mandate</PrimaryButton>}
          />
        </div>
      ) : (
        <AppCard className="mt-4">
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <label className="block font-body text-sm text-[var(--text-sec)]">
              Who you talked with
              <Select value={stakeholderId} onChange={(e) => setStakeholderId(e.target.value)} className="mt-1 w-full">
                <option value="">Choose a person</option>
                {stakeholders.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.role ? `, ${s.role}` : ''}
                  </option>
                ))}
              </Select>
            </label>
            <label className="block font-body text-sm text-[var(--text-sec)]">
              What you heard
              <textarea value={heard} onChange={(e) => setHeard(e.target.value)} rows={3} className={`mt-1 ${FIELD}`} />
            </label>
            {HEARD_KINDS.map((kind) => (
              <label key={kind} className="block font-body text-sm text-[var(--text-sec)]">
                {KIND_LABELS[kind]} it raised <span className="text-[var(--text-ter)]">(optional, becomes an item)</span>
                <input
                  value={raised[kind]}
                  onChange={(e) => setRaised((r) => ({ ...r, [kind]: e.target.value }))}
                  className={`mt-1 ${FIELD}`}
                />
              </label>
            ))}
            {error ? <p role="alert" className="font-body text-sm text-[var(--coral)]">{error}</p> : null}
            {saved ? <p className="font-body text-sm text-[var(--text-sec)]">Saved your conversation with {saved}.</p> : null}
            <PrimaryButton type="submit" disabled={busy || !person || !heard.trim()}>
              {busy ? 'Saving…' : 'Save the conversation'}
            </PrimaryButton>
          </form>
        </AppCard>
      )}

      {logged.length > 0 ? (
        <section className="mt-6">
          <Eyebrow>Conversations so far</Eyebrow>
          <ul className="mt-2 space-y-2">
            {logged.map((c, index) => (
              <li key={`${c.name}-${index}`} className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-[var(--rule)] dark:bg-[var(--surface)]">
                <p className="font-body text-xs text-[var(--text-ter)]">
                  {c.name} · {c.day}
                </p>
                <p className="mt-0.5 font-body text-sm text-[var(--text-pri)]">{c.heard}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mt-6">
        <Eyebrow>Questions to ask</Eyebrow>
        <div className="mt-2 grid gap-3 sm:grid-cols-2">
          {LISTENING_TOUR_QUESTIONS.map((group) => (
            <AppCard key={group.category}>
              <p className="font-heading font-semibold text-sm text-[var(--text-pri)]">{group.category}</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 font-body text-sm text-[var(--text-sec)]">
                {group.questions.map((q) => (
                  <li key={q}>{q}</li>
                ))}
              </ul>
            </AppCard>
          ))}
        </div>
      </section>
    </AppShell>
  );
}
